import { useMediaQuery } from '@mui/material';
import { CSSProperties } from 'react';

// Sizing helpers for tables that opt into `tableLayout: 'fixed'`. A column's `styles` land on an
// inline span around the cell content, where a width does nothing, so pinned widths go on the
// header cell via `headerStyles`. Unpinned columns share whatever width is left.
//
// Fixed-format columns (dates, statuses, icons, codes, mobiles) are pinned at one width everywhere.
// Variable text (names, emails, IDs, barcodes) is either left unpinned to share the slack or, when
// pinned, sized with `useTextColumnWidth` so it grows on ultra-wide screens. Every table keeps at
// least one unpinned column: with none, the browser spreads the slack across the pinned ones too.

/** Header-cell width for a column whose content is a known size; minWidth stops it being squeezed. */
export const pinned = (width: string): CSSProperties => ({ width, minWidth: width });

/** Cell style letting a value with no break points (an email, a UUID, a long name) wrap inside its column. */
export const WRAP_ANYWHERE: CSSProperties = { display: 'block', overflowWrap: 'anywhere' };

/** Widths for column kinds that recur across list tables, so like columns match everywhere. */
export const COLUMN_WIDTHS = {
  icon: '56px',
  dateTime: '135px',
  /** The status badge has a 140px minimum plus padding and never wraps. */
  status: '170px',
  notifications: '56px',
  /** A tenant logo with its name wrapping beneath. */
  by: '120px'
} as const;

/** Viewport width from which list tables switch to their ultra-wide widths: above 1920 HD, catching a 16" MacBook Pro at More Space (2056) and ultra-wide monitors. */
export const ULTRA_WIDE_MIN_WIDTH = 2050;

/** True on ultra-wide screens, where columns sized for HD look starved next to the ones sharing the spare width. */
export const useIsUltraWide = (): boolean => useMediaQuery(`(min-width: ${ULTRA_WIDE_MIN_WIDTH}px)`);

/** How much a pinned variable-text column grows on ultra-wide screens. */
export const ULTRA_WIDE_SCALE = {
  /** Plain text such as an external ID. */
  text: 2,
  /** A logo with its name beneath, or a short label: the logo and short values gain less from the room. */
  logoOrLabel: 1.5
} as const;

/**
 * Width for a pinned column of variable text: the given px width, scaled up on ultra-wide screens.
 * Returns the width rather than styles so a cell that truncates to it can use the same value.
 */
export function useTextColumnWidth() {
  const isUltraWide = useIsUltraWide();
  return (width: string, scale: keyof typeof ULTRA_WIDE_SCALE = 'text'): string =>
    isUltraWide ? `${parseFloat(width) * ULTRA_WIDE_SCALE[scale]}px` : width;
}
